import React from 'react';

import { useIntl } from 'react-intl-next';

import { Box, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useColorStore } from '@atlassian/eoc-color-store';
import { useResponderNames } from '@atlassian/eoc-responder-names';

import { Responder } from '../../responder';

import messages from './messages';
import { RuleIcon } from './rule-icon';
import {
	Container,
	DashedLines,
	IconWrapper,
	Rectangle,
	RuleBody,
	RuleDetail,
	TimeText,
} from './styled';
import { type Props } from './types';
import { widthGenerator } from './utils';

export const Rule = ({ rule }: Props) => {
	const { formatMessage } = useIntl();

	const { getColor } = useColorStore();
	const { backgroundColor } = getColor(rule.notifyId);

	const [responder] = useResponderNames(
		rule.inlineResponder ? [] : [{ id: rule.notifyId, type: rule.type }],
	);

	return (
		<Container>
			<IconWrapper>
				<RuleIcon type={rule.type} />
			</IconWrapper>
			<TimeText>
				{formatMessage(messages.minuteAbbreviation, {
					time: rule.time,
					span: (str: React.ReactNode) => (
						<Text size="small" color="color.text.subtle">
							{str}
						</Text>
					),
				})}
			</TimeText>
			<RuleBody>
				{rule.time <= 30 ? (
					<Rectangle width={widthGenerator(rule.time)} backgroundColor={backgroundColor} />
				) : (
					<>
						<Rectangle width={widthGenerator(rule.time)} backgroundColor={backgroundColor} />
						<Rectangle width={10} backgroundColor={backgroundColor} />
						<Rectangle width={10} backgroundColor={backgroundColor} />
						<Rectangle width={10} backgroundColor={backgroundColor} />
						<Rectangle width={40} backgroundColor={backgroundColor} />
					</>
				)}
			</RuleBody>
			<DashedLines />
			<RuleDetail>
				{formatMessage(messages['ruleVerbalForm'], {
					type: rule.type,
					notifyType: rule.notifyType,
					notifyCondition: rule.notifyCondition.split('-').join(''),
					strong: (str: React.ReactNode) => (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						<Box as="span" style={{ marginLeft: token('space.negative.050', '-4px') }}>
							<Text as="strong">{str}</Text>
						</Box>
					),
					responder: () => <Responder data={responder} inlineResponder={rule.inlineResponder} />,
				})}
			</RuleDetail>
		</Container>
	);
};
